import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import loadable from '@loadable/component';
import { Button, Popup } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';

import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';

import codeSVG from '@plone/volto/icons/code.svg';
import showSVG from '@plone/volto/icons/show.svg';
import indentSVG from '@plone/volto/icons/indent.svg';

const Editor = loadable(() => import('react-simple-code-editor'));

const messages = defineMessages({
  source: { id: 'Source', defaultMessage: 'Código-fonte' },
  preview: { id: 'Preview', defaultMessage: 'Pré-visualizar' },
  prettier: { id: 'Prettify your code', defaultMessage: 'Indentar o código' },
});

/**
 * Edição do campo em **código-fonte HTML**, com o mesmo editor do bloco
 * HTML do Volto (realce de sintaxe, pré-visualização e indentação).
 *
 * Motivo de não usar o editor visual (slate): ele só representa o que tem
 * equivalente no modelo interno dele e, ao editar, reserializa a partir
 * desse modelo — descartando o resto. Medido numa cópia de conteúdo real
 * da migração (`/o-mpf/concursos/.../31o-concurso`): bastou digitar **um
 * caractere** e salvar para o HTML cair de 4.196 para 2.658 caracteres,
 * levando junto os 16 `<div>` estruturais, as duas listas, as classes
 * `conc-proc` (ganchos do CSS) e os dados do concurso ("Subsídio
 * inicial", "R$ 39.753,22", "Vagas", "29 de junho"). Sem versionamento
 * neste tipo, a perda é irreversível.
 *
 * Aqui não há round-trip por modelo intermediário: o que está no editor é
 * exatamente o que vai para o banco. Verificado com o mesmo teste —
 * estrutura e conteúdo preservados, e a edição pretendida aplicada.
 *
 * Solução definitiva continua sendo migrar essa página para blocos Volto;
 * aí o campo volta a ser só o texto de abertura curto.
 */
class HtmlSourceWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isPreview: false };
  }

  getValue() {
    const { value } = this.props;
    if (typeof value === 'string') return value;
    return value?.data || '';
  }

  getContentType() {
    const { value } = this.props;
    return (
      (typeof value === 'object' && value?.['content-type']) || 'text/html'
    );
  }

  onChangeCode = (code) => {
    const { id, onChange } = this.props;
    // Campo vazio volta a ser nulo, como nas demais Collections — evita
    // gravar `<p></p>`, que conta como "tem conteúdo" na listagem.
    onChange(
      id,
      code && code.trim()
        ? { 'content-type': this.getContentType(), data: code }
        : null,
    );
  };

  prettify = async () => {
    const { prettierStandalone, prettierParserHtml } = this.props;
    if (!prettierStandalone?.format) return;
    try {
      const code = (
        await prettierStandalone.format(this.getValue(), {
          parser: 'html',
          plugins: [prettierParserHtml],
        })
      ).trim();
      this.onChangeCode(code);
    } catch (ex) {
      // HTML inválido enquanto o editor digita — mantém o texto como está
      // em vez de rejeitar a edição.
    }
  };

  highlight = (code) => {
    const { prismCore } = this.props;
    if (!prismCore?.highlight || !prismCore?.languages?.html) return code;
    return prismCore.highlight(code, prismCore.languages.html, 'html');
  };

  render() {
    const { id, intl } = this.props;
    const { isPreview } = this.state;
    const value = this.getValue();

    return (
      <FormFieldWrapper {...this.props} className="mpf-html-source">
        <div className="mpf-html-source__toolbar">
          <Popup
            trigger={
              <Button
                type="button"
                icon
                basic
                active={!isPreview}
                aria-label={intl.formatMessage(messages.source)}
                onClick={() => this.setState({ isPreview: false })}
              >
                <Icon name={codeSVG} size="20px" />
              </Button>
            }
            content={intl.formatMessage(messages.source)}
            size="mini"
          />
          <Popup
            trigger={
              <Button
                type="button"
                icon
                basic
                active={isPreview}
                aria-label={intl.formatMessage(messages.preview)}
                onClick={() => this.setState({ isPreview: true })}
              >
                <Icon name={showSVG} size="20px" />
              </Button>
            }
            content={intl.formatMessage(messages.preview)}
            size="mini"
          />
          <Popup
            trigger={
              <Button
                type="button"
                icon
                basic
                aria-label={intl.formatMessage(messages.prettier)}
                onClick={this.prettify}
              >
                <Icon name={indentSVG} size="20px" />
              </Button>
            }
            content={intl.formatMessage(messages.prettier)}
            size="mini"
          />
        </div>

        {isPreview ? (
          <div
            className="mpf-html-source__preview"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: value }}
          />
        ) : (
          <Editor
            value={value}
            id={`field-${id}`}
            name={id}
            onValueChange={this.onChangeCode}
            highlight={this.highlight}
            padding={12}
            className="html-editor"
            ignoreTabKey
          />
        )}
      </FormFieldWrapper>
    );
  }
}

HtmlSourceWidget.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

/**
 * `prismCore` sozinho não conhece HTML — a gramática vem de
 * `prism-markup`, carregada sob demanda. Sem esperar por ela o editor
 * renderiza sem realce no primeiro passe. Mesmo padrão do bloco HTML.
 */
const withPrismMarkup = (WrappedComponent) => (props) => {
  const [loaded, setLoaded] = React.useState(false);
  const cancelled = React.useRef(false);

  React.useEffect(() => {
    import('prismjs/components/prism-markup').then(() => {
      if (!cancelled.current) setLoaded(true);
    });
    return () => {
      cancelled.current = true;
    };
  }, []);

  return loaded ? <WrappedComponent {...props} /> : null;
};

export default compose(
  injectLazyLibs(['prettierStandalone', 'prettierParserHtml', 'prismCore']),
  withPrismMarkup,
  injectIntl,
)(HtmlSourceWidget);
